/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
  IconDownload,
  IconAdjustmentsHorizontal,
  IconRefresh,
  IconX,
  IconClipboardList,
  IconEye,
  IconTrash,
  IconEdit,
  IconSearch,
  IconPackage,
} from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import FilterSidebar from "./FilterSidebar";
import toast from "react-hot-toast";
import useDebounce from "../../../utilities/useDebounce";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import NoDataFound from "../../../components/common/NoDataFound";
import { useSelector } from "react-redux";
import AllPicklistTable, { Picklist } from "./AllPicklistTable";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NewSelectInput from "../../../components/NewSelectInput";
import * as XLSX from "xlsx";

import {
  usePicklistsQuery,
  useDeletePicklistMutation,
  useUpdatePicklistStatusMutation,
} from "../../../apis/picklistApis";
import PrintModal from "./PrintModal";
import AssignStaffModal from "./AssignStaffModal";
import DeletePickingModal from "./DeletePickingModal";
import RoleToggle from "../../Dashboard/_components/RoleToggle";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import PickingDetails from "./PickingDetails";
import BulkStatusUpdateModal from "../../CreatedOrders/_components/BulkStatusUpdateModal";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  filters?: Array<Record<string, string>>;
  setFilters?: (filters: Array<Record<string, string>>) => void;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

const formatPickTime = (minutes: number): string => {
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  }
  return `${minutes}m`;
};

const AllPicklists: React.FC<Props> = ({
  filters,
  setFilters,
  setHeaderActions,
  setHeaderButtons,
}) => {
  const { allowed: canUpdateOrder } = usePermission("orders", "update");
  const { allowed: canDeleteOrder } = usePermission("orders", "delete");
  const { t } = useLanguage();

  const [pageNo, setPageNo] = useState(1);
  const [limitPage, setLimitPage] = useState(10);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchField, setSearchField] = useState("picklistNumber");
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedPicklists, setSelectedPicklists] = useState<any[]>([]);
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePicklistId, setDeletePicklistId] = useState<string | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [viewingPicklistId, setViewingPicklistId] = useState<string | null>(
    null,
  );
  const [viewingPicklistNumber, setViewingPicklistNumber] = useState<
    string | null
  >(null);
  const [showPickingDetails, setShowPickingDetails] = useState(false);
  const [currentAssignedId, setCurrentAssignedId] = useState<string | null>(
    null,
  );
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState("PICKLIST_GENERATED");
  const [activeStatusFilter, setActiveStatusFilter] = useState("ALL");
  const debouncedSearch = useDebounce(search, 500);

  const { currentUser } = useSelector((state: any) => state.auth);

  const [columns, setColumns] = useState([
    { id: 1, text: "SN", value: "sn", isShow: true },
    { id: 2, text: "Picklist Number", value: "picklistNumber", isShow: true },
    { id: 5, text: "Total Orders", value: "totalOrders", isShow: true },
    { id: 6, text: "Picker", value: "pickerName", isShow: true },
    { id: 8, text: "Created By", value: "createdByName", isShow: true },
    { id: 9, text: "Assigned To", value: "assignedToName", isShow: false },
    { id: 10, text: "Created Date", value: "createdAt", isShow: true },
    { id: 11, text: "Started Date", value: "startedAt", isShow: false },
    { id: 12, text: "Completed Date", value: "completedAt", isShow: false },
    { id: 13, text: "Barcode", value: "barcode", isShow: false },
    { id: 14, text: "Notes", value: "notes", isShow: false },
    { id: 4, text: "Status", value: "status", isShow: true },
  ]);

  const showRightPanel = showBulkStatusModal;

  // Track initial/default columns to restore them when sidebar is closed
  const [defaultVisibleColumns, setDefaultVisibleColumns] = useState<string[]>(
    [],
  );
  useEffect(() => {
    if (columns.length > 0 && defaultVisibleColumns.length === 0) {
      const visible = columns
        .filter((col) => col.isShow)
        .map((col) => col.value);
      setDefaultVisibleColumns(visible);
    }
  }, [columns]);

  // Handle auto-shrinking table columns when right sidebar is open
  useEffect(() => {
    if (showRightPanel) {
      const essentialColumns = [
        "sn",
        "picklistNumber",
        "totalOrders",
        "pickerName",
        "status",
      ];
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: essentialColumns.includes(col.value),
        })),
      );
    } else if (defaultVisibleColumns.length > 0) {
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: defaultVisibleColumns.includes(col.value),
        })),
      );
    }
  }, [showRightPanel, defaultVisibleColumns]);

  // Real API data fetching using React Query
  const {
    data: apiPicklistsData,
    isLoading,
    refetch,
  } = usePicklistsQuery({
    page: pageNo,
    limit: limitPage,
    search: debouncedSearch,
    searchField: searchField,
    ...(activeStatusFilter !== "ALL"
      ? { status: activeStatusFilter as any }
      : {}),
    ...filters?.reduce(
      (acc, filter) => {
        Object.assign(acc, filter);
        return acc;
      },
      {} as Record<string, string>,
    ),
  });

  // Transform API data to match table component structure
  const picklistsData = apiPicklistsData
    ? {
        picklists:
          apiPicklistsData.data?.map((picklist: any, index: number) => ({
            ...picklist,
            picklistNumber: picklist.picklistNumber,
            sn: (pageNo - 1) * limitPage + index + 1,
            warehouseName: picklist.warehouseId || "",
            createdByName: picklist.creator
              ? picklist.creator.details?.fullName || picklist.creator.email
              : "",
            assignedToName: picklist.assignee
              ? picklist.assignee.fullName ||
                picklist.assignee.details?.fullName ||
                picklist.assignee.email
              : "",
            pickerName: picklist.assignee
              ? picklist.assignee.fullName ||
                picklist.assignee.details?.fullName ||
                picklist.assignee.email
              : "",
            barcode: picklist.picklistNumber, // Using picklistNumber as barcode
            startedAt: picklist.startedAt || "",
            completedAt: picklist.completedAt || "",
            notes: picklist.notes || "",
          })) || [],
        pagination: apiPicklistsData.pagination,
      }
    : null;

  // Calculate dynamic stats from the current page's picklists
  const picklistsList = picklistsData?.picklists || [];

  const totalPicklists = apiPicklistsData?.pagination?.total ?? 0;

  const totalOrders = picklistsList.reduce(
    (acc: number, curr: any) => acc + (Number(curr.totalOrders) || 0),
    0,
  );

  const completedScans = picklistsList.reduce(
    (acc: number, curr: any) => acc + (Number(curr.completedOrders) || 0),
    0,
  );

  const pendingOrders = Math.max(0, totalOrders - completedScans);

  const completionRate =
    totalOrders > 0
      ? Math.min(
          100,
          Math.max(0, Math.round((completedScans / totalOrders) * 100)),
        )
      : 0;

  const displayCompletedScans = Math.min(completedScans, totalOrders);

  const listsNeedingReview = picklistsList.filter(
    (p: any) =>
      !["COMPLETED", "CANCELLED", "PICKED"].includes(p.status) &&
      Number(p.totalOrders) > Number(p.completedOrders),
  ).length;

  const completedPicklistsWithTime = picklistsList.filter(
    (p: any) => p.startedAt && p.completedAt,
  );

  const avgPickTimeMin =
    completedPicklistsWithTime.length > 0
      ? Math.round(
          completedPicklistsWithTime.reduce((acc: number, p: any) => {
            const duration =
              new Date(p.completedAt).getTime() -
              new Date(p.startedAt).getTime();
            return acc + duration;
          }, 0) /
            (completedPicklistsWithTime.length * 60000),
        )
      : 0;

  const completedPicklists = apiPicklistsData?.statusCounts?.completed ?? 0;
  const generatedPicklists = apiPicklistsData?.statusCounts?.generated ?? 0;
  const inProgressPicklists = apiPicklistsData?.statusCounts?.in_progress ?? 0;

  // Delete picklist mutation
  const deletePicklistMutate = useDeletePicklistMutation();
  const updateStatusMutate = useUpdatePicklistStatusMutation();

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const handleSetHeadings = (value: string | undefined, checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectPicklist = (picklist: any) => {
    if (selectedPicklists.find((p) => p.id === picklist.id)) {
      setSelectedPicklists(
        selectedPicklists.filter((p) => p.id !== picklist.id),
      );
    } else {
      setSelectedPicklists([...selectedPicklists, picklist]);
    }
  };

  const handleSelectAll = () => {
    if (selectedPicklists.length === picklistsData?.picklists?.length) {
      setSelectedPicklists([]);
    } else {
      setSelectedPicklists(picklistsData?.picklists || []);
    }
  };

  const handleDeletePicklist = (picklistId: string) => {
    if (!canDeleteOrder && !canUpdateOrder) {
      toast.error("You do not have permission to delete picklists");
      return;
    }
    setDeletePicklistId(picklistId);
    setShowDeleteModal(true);
  };

  const confirmDeletePicklist = () => {
    if (deletePicklistId) {
      deletePicklistMutate.mutate(deletePicklistId, {
        onSuccess: () => {
          toast.success("Picklist deleted successfully");
          setShowDeleteModal(false);
          setDeletePicklistId(null);
          refetch();
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to delete picklist";
          toast.error(errorMessage);
        },
      });
    }
  };

  const handleUpdateToPicked = (picklist: Picklist) => {
    if (!canUpdateOrder) {
      toast.error("You do not have permission to update picklists");
      return;
    }
    updateStatusMutate.mutate(
      { id: picklist.id, status: "PICKED" },
      {
        onSuccess: () => {
          toast.success("Picklist updated to picked successfully");
          refetch();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update picklist to picked");
        },
      },
    );
  };

  const handleExportExcel = () => {
    const dataToExport =
      selectedPicklists.length > 0
        ? selectedPicklists
        : picklistsData?.picklists || [];

    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      dataToExport.map((picklist: any) => ({
        "Picklist Number": picklist.picklistNumber,
        Status: picklist.status,
        "Total Orders": picklist.totalOrders,
        "Created Date": picklist.createdAt,
        "Created By": picklist.createdByName,
        Picker: picklist.pickerName,
        Warehouse: picklist.warehouseName,
        Barcode: picklist.barcode,
        Notes: picklist.notes,
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Picklists");
    XLSX.writeFile(
      workbook,
      `picklists_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    toast.success("Picklists exported successfully");
  };

  const handleRefresh = () => {
    refetch();
    toast.success("Picklists refreshed successfully");
  };

  const handleSelect = (value: string) => {
    setSelectedOption(value);
    setOpen(false);
    if (value === "exportExcel") {
      handleExportExcel();
    } else if (value === "deleteSelected") {
      // Implement delete selected
    } else if (value === "assignTo") {
      if (selectedPicklists.length === 0) {
        toast.error("Please select at least one picklist to assign");
        return;
      }
      toast("Assign To feature coming soon!", { icon: "ℹ️" });
    }
  };

  const options = [
    { text: "Export Excel", value: "exportExcel" },
    { text: "Delete Selected", value: "deleteSelected" },
    { text: "Assign To", value: "assignTo" },
  ];

  useEffect(() => {
    if (setHeaderActions) setHeaderActions(null);
  }, [setHeaderActions]);

  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-xs select-none font-bold">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Generated")}:</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {generatedPicklists}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("In Progress")}:</span>
          <span className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
            {inProgressPicklists}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Completed")}:</span>
          <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded">
            {completedPicklists}
          </span>
        </div>
      </div>,
    );

    return () => setHeaderButtons(null);
  }, [
    setHeaderButtons,
    generatedPicklists,
    inProgressPicklists,
    completedPicklists,
    t,
  ]);

  if (showPickingDetails && viewingPicklistId) {
    return (
      <PickingDetails
        picklistId={viewingPicklistId}
        onBack={() => {
          setShowPickingDetails(false);
          setViewingPicklistId(null);
          setViewingPicklistNumber(null);
        }}
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        {/* Left Side: Table & Filters */}
        <div
          className={
            showRightPanel
              ? "col-span-12 lg:col-span-7 xl:col-span-8 space-y-6"
              : "col-span-12 space-y-6"
          }
        >
          {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
          <div className="bg-white rounded-3xl overflow-visible">
            {/* ── Header Row (Filters + Actions Unified) ──────────────── */}
            <div className="px-6 py-1.5">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Left side: Brand badge + Status dropdown */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Status dropdown */}
                </div>

                {/* Right side: Search + Bulk status + Export + Entries + Columns Filter */}
                <div className="flex items-center flex-wrap gap-2.5">
                  <NewSelectInput
                    name="status-filter"
                    placeholder={t("Status")}
                    value={
                      activeStatusFilter === "ALL" ? "" : activeStatusFilter
                    }
                    setValue={(val) => {
                      setActiveStatusFilter(val || "ALL");
                      setPageNo(1);
                    }}
                    selectOptions={[
                      { id: "ALL", value: "ALL", text: t("All") },
                      {
                        id: "GENERATED",
                        value: "GENERATED",
                        text: t("Generated"),
                      },
                      {
                        id: "IN_PROGRESS",
                        value: "IN_PROGRESS",
                        text: t("In Progress"),
                      },
                      {
                        id: "COMPLETED",
                        value: "COMPLETED",
                        text: t("Completed"),
                      },
                    ]}
                    className="w-36 font-semibold"
                    removeDiv
                    compact
                    dropdownAlign="left"
                  />
                  {/* Sliding Search */}
                  <div className="relative flex items-center group">
                    <div
                      onClick={() => {
                        if (!showSearch) {
                          setShowSearch(true);
                        }
                      }}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                        showSearch
                          ? "w-64 px-2 cursor-default"
                          : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSearch(!showSearch);
                        }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                      >
                        <IconSearch size={17} stroke={2.5} />
                      </button>
                      {showSearch && (
                        <>
                          <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("Search...")}
                            autoFocus
                            className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearch("");
                              setShowSearch(false);
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                            title={t("Clear & Close")}
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        </>
                      )}
                    </div>
                    {!showSearch && (
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                        {t("Search")}
                      </div>
                    )}
                  </div>

                  {/* Bulk Generate */}
                  {canUpdateOrder && (
                    <div className="relative group">
                      <button
                        onClick={() => {
                          setTargetStatus("PICKLIST_GENERATED");
                          setShowBulkStatusModal(true);
                        }}
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                      >
                        <IconPackage size={17} stroke={2.5} />
                      </button>
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                        {t("Bulk Generate Picklist")}
                      </div>
                    </div>
                  )}

                  {/* Export Excel */}
                  <div className="relative group">
                    <button
                      onClick={handleExportExcel}
                      className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer relative"
                    >
                      <IconDownload size={17} stroke={2.5} />
                      {selectedPicklists.length > 0 && (
                        <span className="absolute -top-1 -right-1 text-[8px] bg-[#FBD037] text-[#131327] w-4 h-4 rounded-full flex items-center justify-center font-bold shadow-sm border border-white">
                          {selectedPicklists.length}
                        </span>
                      )}
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Export to Excel")}
                    </div>
                  </div>

                  {/* Row dropdown (Entries selector) */}
                  <EntriesSelector
                    value={limitPage}
                    options={[10, 20, 30]}
                    onChange={(val) => {
                      setLimitPage(val);
                      setPageNo(1);
                    }}
                  />

                  {/* Table filter icon (ColumnsFilter) */}
                  <div className="relative group">
                    <ColumnsFilter
                      columnsData={columns}
                      setHeadings={handleSetHeadings}
                    />
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Columns View")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Table Container ─────────────────────────────────────── */}
            <div className="border-t border-slate-100">
              {isLoading ? (
                <TableSkeleton
                  columns={columns
                    .filter((col) => col.isShow)
                    .map((col) => col.text || "")}
                />
              ) : picklistsData?.picklists ? (
                <AllPicklistTable
                  data={picklistsData}
                  columnsData={columns.filter((col) => col.isShow)}
                  currentPage={pageNo}
                  limitPage={limitPage}
                  isCheckboxEnabled={isCheckboxEnabled}
                  selectedPicklists={selectedPicklists}
                  onSelectPicklist={handleSelectPicklist}
                  onDeletePicklist={handleDeletePicklist}
                  onAssignPicklist={(picklist: Picklist) => {
                    if (!canUpdateOrder) {
                      toast.error(
                        "You do not have permission to assign picklists",
                      );
                      return;
                    }
                    setViewingPicklistId(picklist.id);
                    setViewingPicklistNumber(picklist.picklistNumber);
                    setCurrentAssignedId(picklist.assignedTo || null);
                    setShowAssignModal(true);
                  }}
                  onPrintPicklist={(picklist: Picklist) => {
                    setViewingPicklistId(picklist.id);
                    setViewingPicklistNumber(picklist.picklistNumber);
                    setShowPrintModal(true);
                  }}
                  onViewDetails={(picklist: Picklist) => {
                    setViewingPicklistId(picklist.id);
                    setViewingPicklistNumber(picklist.picklistNumber);
                    setShowPickingDetails(true);
                  }}
                  currentUser={currentUser}
                  onUpdateToPicked={handleUpdateToPicked}
                />
              ) : null}
            </div>
          </div>

          {/* ── Pagination ───────────────────────────────────────────── */}
        </div>{" "}
        {/* Left Side End */}
        {showBulkStatusModal && (
          <BulkStatusUpdateModal
          onClose={() => setShowBulkStatusModal(false)}
          fetchData={refetch}
          targetStatus={targetStatus}
          />
        )}
      </div>
        {picklistsData?.pagination?.totalPages &&
          picklistsData.pagination.totalPages > 1 && (
            <div className="py-4">
              <Pagination
                currentPage={pageNo}
                totalPages={picklistsData.pagination.totalPages}
                totalItems={totalPicklists}
                itemLabel="picklists"
                fetchData={handlePaginationFetch}
              />
            </div>
          )}

      {/* Delete Confirmation Modal */}
      <DeletePickingModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeletePicklist}
        pickupId={deletePicklistId}
      />

      {/* Print Modal */}
      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        picklistId={viewingPicklistId}
      />

      {/* Assign Staff Modal */}
      <AssignStaffModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        picklistId={viewingPicklistId}
        currentAssignedId={currentAssignedId}
      />
    </>
  );
};

export default AllPicklists;
